import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from '@mui/joy'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import AppBanner from '../../components/AppBanner/AppBanner'
import { usePostHogReachability } from './PostHogReachabilityContext'
import { POSTHOG_API_HOST, colors } from '../../constants'

export const POSTHOG_HOST_LABEL = POSTHOG_API_HOST.replace('https://', '')

export const POSTHOG_BLOCKED_BANNER_TITLE = 'Some features may be missing'
export const POSTHOG_BLOCKED_BANNER_MESSAGE = `A required service is blocked in this browser or network. Ask IT to allow ${POSTHOG_HOST_LABEL}, or disable shields for this site if you use Brave.`

const COPIED_FEEDBACK_MS = 2000

const PostHogHostLabel = (): React.ReactElement => {
  const [isCopied, setIsCopied] = useState(false)
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current != null) {
        clearTimeout(copiedTimeoutRef.current)
      }
    }
  }, [])

  const handleCopy = useCallback(async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(POSTHOG_HOST_LABEL)
    } catch {
      return
    }

    setIsCopied(true)
    if (copiedTimeoutRef.current != null) {
      clearTimeout(copiedTimeoutRef.current)
    }
    copiedTimeoutRef.current = setTimeout(() => {
      setIsCopied(false)
      copiedTimeoutRef.current = null
    }, COPIED_FEEDBACK_MS)
  }, [])

  return (
    <Link
      component="button"
      type="button"
      underline="hover"
      onClick={handleCopy}
      aria-label={isCopied ? 'Copied' : `Copy ${POSTHOG_HOST_LABEL}`}
      data-testid="posthog-blocked-banner-host"
      endDecorator={
        isCopied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />
      }
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'baseline',
        fontFamily: 'monospace',
        fontWeight: 'lg',
        fontSize: 'inherit',
        color: colors.NODAFI_BLUE,
        '&:hover, &:active': {
          color: colors.NODAFI_BLUE,
        },
      }}
    >
      {POSTHOG_HOST_LABEL}
    </Link>
  )
}

const PostHogBlockedBannerMessage = (): React.ReactElement => {
  const [messageBeforeHost, messageAfterHost] =
    POSTHOG_BLOCKED_BANNER_MESSAGE.split(POSTHOG_HOST_LABEL)

  return (
    <>
      {messageBeforeHost}
      <PostHogHostLabel />
      {messageAfterHost}
    </>
  )
}

export const PostHogBlockedBanner = React.memo(() => {
  const { isBlocked } = usePostHogReachability()
  const [isDismissed, setIsDismissed] = useState(false)

  const handleDismiss = useCallback(() => {
    setIsDismissed(true)
  }, [])

  if (isBlocked === false || isDismissed) return null

  return (
    <AppBanner
      testId="posthog-blocked-banner"
      color="warning"
      title={POSTHOG_BLOCKED_BANNER_TITLE}
      message={<PostHogBlockedBannerMessage />}
      onDismiss={handleDismiss}
    />
  )
})

PostHogBlockedBanner.displayName = 'PostHogBlockedBanner'
